import { CustomFieldsMapper, LocationMapper, ReportMapper } from '@nx-smartmonkey/shared/domain';
import { ProjectCustomFields } from '../../domain/project/ProjectCustomFields';
import { Stop } from '../../domain/stop/Stop';
import { StopDTO } from '../../domain/stop/StopDTO';
import { StopRepositoryDTO } from '../../domain/stop/StopRepositoryDTO';
import { CustomersSurveyMapper } from '../customersSurvey/CustomersSurveyMapper';
import { EventMapper } from '../event/EventMapper';
import { TaskMapper } from '../task/TaskMapper';

export class StopMapper {
  static toDomain(props: StopDTO, projectCustomFields: ProjectCustomFields): Stop {
    const {
      id,
      project_id,
      plan_id,
      client_id,
      client_external_id,
      label,
      external_id,
      type,
      status,
      location,
      location_details,
      comments,
      reference_person,
      phone,
      email,
      url,
      custom_fields,
      weight,
      volume,
      time_windows,
      requires,
      duration,
      max_delivery_time,
      reward,
      cluster,
      driver_id,
      vehicle_id,
      order,
      planned_arrival_time,
      planned_departure_time,
      distance_to_previous_stop,
      distance_to_next_stop,
      estimated_arrival_time,
      estimated_departure_time,
      pickup_id,
      pickup,
      tasks,
      events,
      reports,
      survey,
      pin,
      customer_text_field,
      supervisors,
      deleted,
      created_by,
      created_at,
      updated_at,
      deleted_at,
    } = props;

    const locationDomain = location ? LocationMapper.toDomain(location) : undefined;

    return Stop.create(
      {
        project_id,
        plan_id,
        client_id,
        client_external_id,
        label,
        external_id,
        type,
        status,
        location: locationDomain,
        location_details,
        comments,
        reference_person,
        phone,
        email,
        url,
        custom_fields: custom_fields
          ? CustomFieldsMapper.toDomain(custom_fields, projectCustomFields.client)
          : undefined,
        weight,
        volume,
        time_windows,
        requires,
        duration,
        max_delivery_time,
        reward,
        cluster,
        driver_id,
        vehicle_id,
        order,
        planned_arrival_time,
        planned_departure_time,
        distance_to_previous_stop,
        distance_to_next_stop,
        estimated_arrival_time,
        estimated_departure_time,
        pickup_id,
        pickup: pickup ? StopMapper.toDomain(pickup, projectCustomFields) : undefined,
        tasks: tasks?.map((task) => TaskMapper.toDomain(task, projectCustomFields.task)),
        events: events?.map(EventMapper.toDomain),
        reports: reports?.map((report) =>
          ReportMapper.toDomain(report, [
            ...projectCustomFields.service_report_completed,
            ...projectCustomFields.service_report_canceled,
            ...projectCustomFields.service_report_task_completed,
            ...projectCustomFields.service_report_task_canceled,
          ])
        ),
        survey: survey ? CustomersSurveyMapper.toDomain(survey) : undefined,
        pin,
        customer_text_field,
        supervisors,
        deleted,
        created_by,
        created_at,
        updated_at,
        deleted_at,
      },
      id
    );
  }

  static toDTO(stop: Stop): StopDTO {
    return {
      id: stop.id,
      project_id: stop.project_id,
      plan_id: stop.plan_id,
      client_id: stop.client_id,
      client_external_id: stop.client_external_id,
      label: stop.label,
      external_id: stop.external_id,
      type: stop.type,
      status: stop.status,
      location: stop.location ? LocationMapper.toDTO(stop.location) : undefined,
      location_details: stop.location_details,
      comments: stop.comments,
      reference_person: stop.reference_person,
      phone: stop.phone,
      email: stop.email,
      url: stop.url,
      custom_fields: stop.custom_fields,
      weight: stop.weight,
      volume: stop.volume,
      time_windows: stop.time_windows,
      requires: stop.requires,
      duration: stop.duration,
      max_delivery_time: stop.max_delivery_time,
      reward: stop.reward,
      cluster: stop.cluster,
      driver_id: stop.driver_id,
      vehicle_id: stop.vehicle_id,
      order: stop.order,
      planned_arrival_time: stop.planned_arrival_time,
      planned_departure_time: stop.planned_departure_time,
      distance_to_previous_stop: stop.distance_to_previous_stop,
      distance_to_next_stop: stop.distance_to_next_stop,
      estimated_arrival_time: stop.estimated_arrival_time,
      estimated_departure_time: stop.estimated_departure_time,
      pickup_id: stop.pickup_id,
      pickup: stop.pickup ? StopMapper.toDTO(stop.pickup) : undefined,
      tasks: stop.tasks?.map(TaskMapper.toDTO),
      events: stop.events?.map(EventMapper.toDTO),
      reports: stop.reports?.map(ReportMapper.toDTO),
      survey: stop.survey ? CustomersSurveyMapper.toDTO(stop.survey) : undefined,
      pin: stop.pin,
      customer_text_field: stop.customer_text_field,
      supervisors: stop.supervisors,
      deleted: stop.deleted,
      created_by: stop.created_by,
      created_at: stop.created_at,
      updated_at: stop.updated_at,
      deleted_at: stop.deleted_at,
    };
  }

  static toRepositoryAsMonkey(stop: Stop): StopRepositoryDTO {
    return {
      client_id: stop.client_id,
      client_external_id: stop.client_external_id,
      label: stop.label,
      external_id: stop.external_id,
      status: stop.status,
      driver_id: stop.driver_id,
      ...(stop.location !== undefined
        ? { location: stop.location !== null ? LocationMapper.toDTO(stop.location) : null }
        : {}),

      // Info
      location_details: stop.location_details,
      comments: stop.comments,
      reference_person: stop.reference_person,
      phone: stop.phone,
      email: stop.email,
      url: stop.url,
      custom_fields: stop.custom_fields ? CustomFieldsMapper.toRepository(stop.custom_fields) : undefined,

      // Constraints
      weight: stop.weight,
      volume: stop.volume,
      time_windows: stop.time_windows,
      requires: stop.requires,
      duration: stop.duration,
      max_delivery_time: stop.max_delivery_time,
      reward: stop.reward,
      cluster: stop.cluster,

      // Operational
      tasks: stop.tasks?.map(TaskMapper.toRepository),
    };
  }

  static toRepository(stop: Stop): StopRepositoryDTO {
    return {
      ...this.toRepositoryAsMonkey(stop),
      supervisors: stop.supervisors,
    };
  }
}
